import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { ComplianceAnswerOption } from 'shared/domain/compliance/complianceAnswerOption';
import { ProtocolItemModel } from 'shared/domain/inspection/inspectionModel';
import { getComplianceResultFromProtocolItem } from 'shared/domain/inspection/protocolItemAccessors';
import { protocolItemsByChecklistItemId } from 'shared/domain/inspection/mapping/protocolItemsByChecklistItemId';
import {
  isComplianceChecklistAnswer,
  isNumericChecklistAnswer,
  isOptionSelectionChecklistAnswer,
  isTextChecklistAnswer,
} from 'shared/domain/template/checklistAnswerGuards';
import { InspectionTemplateModel } from 'shared/domain/template/inspectionTemplateModel';
import { ProtocolAnswerUpdateOutDto } from 'shared/dtos/out/protocolAnswer';
import { HashMap } from 'shared/types/commonView';
import { areArraysShallowEqual } from 'shared/utils/array';
import {
  isRichTextEmpty,
  toPersistedRichTextHtml,
} from 'shared/utils/richText';

import { findProtocolAnswerByChecklistAnswerId } from 'shared/domain/protocolAnswer/findProtocolAnswerByChecklistAnswerId';
import {
  buildComplianceAnswerFormKey,
  buildLegacyComplianceAnswerFormKey,
  buildNumericAnswerFormKey,
  buildOptionSelectionAnswerFormKey,
  buildTextAnswerFormKey,
} from 'shared/domain/protocolAnswer/formKeys';
import {
  isComplianceProtocolAnswer,
  isNumericProtocolAnswer,
  isOptionSelectionProtocolAnswer,
  isTextProtocolAnswer,
} from 'shared/domain/protocolAnswer/types';

export interface ProtocolAnswerSubmitTask {
  checklistItemId: string;
  checklistAnswerId: string;
  body: ProtocolAnswerUpdateOutDto;
}

function normalizedTextValue(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const sanitized = toPersistedRichTextHtml(value);
  return isRichTextEmpty(sanitized) ? null : sanitized;
}

function readComplianceFormValue(
  form: HashMap<unknown>,
  checklistItemId: string,
  answerId: string
): ComplianceAnswerOption | undefined {
  return (
    (form[buildComplianceAnswerFormKey(checklistItemId, answerId)] as
      | ComplianceAnswerOption
      | undefined) ??
    (form[buildLegacyComplianceAnswerFormKey(checklistItemId)] as
      | ComplianceAnswerOption
      | undefined)
  );
}

export function collectProtocolAnswerSubmitTasks(
  form: HashMap<unknown>,
  template: InspectionTemplateModel,
  protocolItems: ProtocolItemModel[] | undefined
): ProtocolAnswerSubmitTask[] {
  const tasks: ProtocolAnswerSubmitTask[] = [];
  const protocolByChecklistItemId = protocolItemsByChecklistItemId(protocolItems);

  template.checklist.forEach((checklistItem) => {
    if (checklistItem.deleted) {
      return;
    }

    const protocolItem = protocolByChecklistItemId.get(checklistItem._id);
    const checklistItemId = checklistItem._id;

    checklistItem.answers?.forEach((answerConfig) => {
      if ('deleted' in answerConfig && answerConfig.deleted) {
        return;
      }

      const persistedAnswer = findProtocolAnswerByChecklistAnswerId(
        protocolItem?.answers,
        answerConfig._id
      );

      if (
        answerConfig.type === AnswerType.Compliance &&
        isComplianceChecklistAnswer(answerConfig)
      ) {
        const formValue = readComplianceFormValue(
          form,
          checklistItemId,
          answerConfig._id
        );

        if (formValue === undefined) {
          return;
        }

        const persistedValue =
          persistedAnswer && isComplianceProtocolAnswer(persistedAnswer)
            ? persistedAnswer.selectedOption
            : protocolItem
              ? getComplianceResultFromProtocolItem(protocolItem)
              : undefined;

        if (formValue === persistedValue) {
          return;
        }

        tasks.push({
          checklistItemId,
          checklistAnswerId: answerConfig._id,
          body: {
            type: AnswerType.Compliance,
            selectedOption: formValue,
          },
        });
        return;
      }

      if (
        answerConfig.type === AnswerType.Numeric &&
        isNumericChecklistAnswer(answerConfig)
      ) {
        const formValue = form[
          buildNumericAnswerFormKey(checklistItemId, answerConfig._id)
        ] as number | undefined;
        const persistedValue =
          persistedAnswer && isNumericProtocolAnswer(persistedAnswer)
            ? persistedAnswer.numericValue
            : undefined;

        if (formValue === persistedValue) {
          return;
        }

        tasks.push({
          checklistItemId,
          checklistAnswerId: answerConfig._id,
          body: {
            type: AnswerType.Numeric,
            numericValue: formValue,
          },
        });
        return;
      }

      if (
        answerConfig.type === AnswerType.Text &&
        isTextChecklistAnswer(answerConfig)
      ) {
        const formValue = normalizedTextValue(
          form[buildTextAnswerFormKey(checklistItemId, answerConfig._id)]
        );
        const persistedValue =
          persistedAnswer && isTextProtocolAnswer(persistedAnswer)
            ? normalizedTextValue(persistedAnswer.textValue)
            : null;

        if (formValue === persistedValue) {
          return;
        }

        tasks.push({
          checklistItemId,
          checklistAnswerId: answerConfig._id,
          body: {
            type: AnswerType.Text,
            textValue: formValue,
          },
        });
        return;
      }

      if (
        answerConfig.type === AnswerType.OptionSelection &&
        isOptionSelectionChecklistAnswer(answerConfig)
      ) {
        const formValue = form[
          buildOptionSelectionAnswerFormKey(checklistItemId, answerConfig._id)
        ] as string[] | undefined;
        const persistedValue =
          persistedAnswer && isOptionSelectionProtocolAnswer(persistedAnswer)
            ? persistedAnswer.selectedOptionIds
            : undefined;

        if (areArraysShallowEqual(formValue ?? [], persistedValue ?? [])) {
          return;
        }

        tasks.push({
          checklistItemId,
          checklistAnswerId: answerConfig._id,
          body: {
            type: AnswerType.OptionSelection,
            selectedOptionIds: formValue ?? [],
          },
        });
      }
    });
  });

  return tasks;
}

export function collectProtocolAnswerSubmitTasksForChecklistItem(
  form: HashMap<unknown>,
  template: InspectionTemplateModel,
  protocolItems: ProtocolItemModel[] | undefined,
  checklistItemId: string
): ProtocolAnswerSubmitTask[] {
  return collectProtocolAnswerSubmitTasks(form, template, protocolItems).filter(
    (task) => task.checklistItemId === checklistItemId
  );
}
